import UnsavedChangesBoundary, { DirtyState, useDraftDirty } from "../common/UnsavedChangesBoundary";
import React from "react";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import ModalA11yWrapper from "../../ui/ModalA11yWrapper";
import RichTextEditor from "../../ui/RichTextEditor";
import { spacing, typography } from "../../ui/tokens";

export default function AssessmentReviewModal({
  open,
  saving,
  title = "Assessment Review Note",
  confirmLabel = "Save Review & Apply Change",
  summary,
  impactToBusiness,
  mitigationPlan,
  nextReviewDate,
  residualScore,
  residualBand,
  movement,
  showReviewDateField = false,
  error,
  onSummaryChange,
  onImpactToBusinessChange,
  onMitigationPlanChange,
  onNextReviewDateChange,
  onCancel,
  onConfirm,
}) {
  const isDirty = useDraftDirty({ summary, impactToBusiness, mitigationPlan }, open);
  if (!open) return null;

  return (
    <UnsavedChangesBoundary onClose={onCancel} itemName="risk">
      {(requestClose) => (
    <ModalA11yWrapper open onClose={requestClose}>
      <div style={modalStyle}>
        <DirtyState isDirty={isDirty} />
        <div style={{ display: "grid", gap: spacing.xs / 2 }}>
          <h3 style={{ margin: 0, fontWeight: typography.weight.bold, fontSize: typography.size.lg }}>
            {title}
          </h3>
          <div style={{ color: "#64748b", fontSize: typography.size.xs }}>
            Review delta: {movement || "remained static"} | Residual rating: {residualScore ?? "-"} ({residualBand || "-"})
          </div>
        </div>

        <div style={fieldStyle}>
          <span style={labelStyle}>Review summary (required)</span>
          <RichTextEditor
            value={summary}
            onChange={onSummaryChange}
            disabled={saving}
            minHeight={140}
          />
        </div>

        <div style={fieldStyle}>
          <span style={labelStyle}>Impact to business</span>
          <RichTextEditor
            value={impactToBusiness}
            onChange={onImpactToBusinessChange}
            disabled={saving}
            minHeight={110}
          />
        </div>

        <div style={fieldStyle}>
          <span style={labelStyle}>Remediation / mitigation plan</span>
          <RichTextEditor
            value={mitigationPlan}
            onChange={onMitigationPlanChange}
            disabled={saving}
            minHeight={110}
          />
        </div>

        {showReviewDateField && (
          <label style={fieldStyle}>
            <span style={labelStyle}>Next review date (required)</span>
            <Input
              type="date"
              value={nextReviewDate}
              onChange={onNextReviewDateChange}
              disabled={saving}
            />
          </label>
        )}

        {error && <div style={{ color: "#b91c1c", fontSize: typography.size.xs }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing.sm }}>
          <Button variant="secondary" onClick={requestClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={saving}>
            {saving ? "Saving..." : confirmLabel}
          </Button>
        </div>
      </div>
    </ModalA11yWrapper>
      )}
    </UnsavedChangesBoundary>
  );
}

const modalStyle = {
  background: "white",
  borderRadius: 12,
  padding: spacing.lg,
  width: 640,
  maxWidth: "92vw",
  display: "grid",
  gap: spacing.md,
};

const fieldStyle = {
  display: "grid",
  gap: spacing.xs / 2,
};

const labelStyle = {
  color: "#334155",
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
};
